import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { CTABand } from "@/components/shared/CTABand";
import { SERVICES, PROCESS_STEPS } from "@/data/services";
import { useSEO } from "@/lib/seo";

export default function TechnicalServices() {
  useSEO({
    title: "Technical Services — Covering, Machining & Refurbishment",
    description:
      "Rubber covering, polyurethane covering, core manufacturing, regrinding, dynamic balancing, roll repair, induction hardening and hard chrome plating — all in-house at Vikas Rolls.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: SERVICES.map((s, i) => ({
        "@type": "Service",
        position: i + 1,
        name: s.name,
        description: s.short,
        provider: { "@type": "Organization", name: "Vikas Rolls Private Limited" },
      })),
    },
  });

  return (
    <>
      <PageHero
        chapter="S"
        label="Technical Services"
        title="Eight Services. One Roof. Full Control."
        sub="From new manufacture to economical refurbishment — every service is carried out in-house, so quality never leaves our hands."
        crumbs={[{ label: "Technical Services" }]}
      />

      <section className="bg-ink py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 space-y-16 md:space-y-24">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug}>
              <article
                id={s.slug}
                data-testid={`service-section-${s.slug}`}
                className={`grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-16 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="corner-marks border border-line overflow-hidden max-w-md">
                  <img src={s.image} alt={`${s.name} at the Vikas Rolls facility`} loading="lazy" className="img-industrial w-full object-cover" />
                </div>
                <div>
                  <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mb-3">Service {s.index}</p>
                  <h2 className="font-display font-black tracking-tight text-2xl md:text-4xl text-steel-light mb-5">{s.name}</h2>
                  <p className="text-steel text-base md:text-lg leading-relaxed mb-6">{s.description}</p>
                  <div className="border-l-2 border-amber-brand pl-5 py-1">
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-steel/70 mb-1.5">Why it matters</p>
                    <p className="text-steel-light text-sm md:text-base leading-relaxed">{s.benefit}</p>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink-2 border-y border-line py-20 md:py-28 bg-blueprint">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <SectionHeading chapter="→" label="How We Work" title="From application review to dispatch." className="mb-14" />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
            {PROCESS_STEPS.map((step) => (
              <StaggerItem key={step.step} className="bg-ink-2">
                <div className="p-7 md:p-8 h-full relative hover:bg-ink-3 transition-colors duration-300">
                  <span className="font-display font-black text-5xl text-transparent absolute top-4 right-5 select-none" style={{ WebkitTextStroke: "1px #33363a" }} aria-hidden="true">
                    {step.step}
                  </span>
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-amber-brand mb-3">Step {step.step}</p>
                  <h3 className="font-display font-bold text-xl text-steel-light mb-2">{step.title}</h3>
                  <p className="text-sm text-steel leading-relaxed">{step.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-12">
            <Link to="/request-a-quote" data-testid="services-quote-button" className="group inline-flex items-center gap-3 bg-amber-brand text-ink font-display font-bold px-8 py-4 text-lg hover:bg-steel-light">
              Start With an Application Review
              <ArrowRight size={19} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>
      <CTABand />
    </>
  );
}
